import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/i18n/translations";
import { RefreshCw, Unplug, ExternalLink, Package, CheckCircle2 } from "lucide-react";

const CURRENCIES = ["EUR", "USD", "RUB", "TRY", "GBP", "CHF", "UAH", "KZT"];

// Settings → business type "Online store": connect the Tilda store catalog via Tilda's official MCP server.
export default function TildaMcpConnect({ API }) {
  const { t } = useTranslation();
  const tf = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState(null);

  const load = () => axios.get(`${API}/tilda/status`).then((r) => setSt(r.data)).catch(() => setSt({ connected: false }));
  useEffect(() => { load(); }, []);

  const errText = (e) => {
    const d = e?.response?.data?.detail || "";
    if (d === "invalid_key" || d === "invalid_key_format") return tf("tilda.err.key", "Ключ не принят Tilda. Проверьте ключ (начинается с mcp_tilda_) и срок его действия");
    if (d === "no_projects") return tf("tilda.err.noProjects", "У ключа нет доступа ни к одному проекту");
    if (d === "tilda_unreachable") return tf("tilda.err.unreachable", "Tilda недоступна, попробуйте позже");
    return d || tf("tilda.err.generic", "Ошибка подключения");
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); setSt(r.data); if (okMsg) toast.success(okMsg); setProducts(null); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const connect = () => run(() => axios.post(`${API}/tilda/connect`, { mcp_key: key.trim() }), tf("tilda.connected", "Tilda подключена, каталог загружен"));
  const sync = () => run(() => axios.post(`${API}/tilda/sync`), tf("tilda.synced", "Каталог обновлён"));
  const patch = (body) => run(() => axios.patch(`${API}/tilda/settings`, body));
  const disconnect = async () => {
    if (!window.confirm(tf("tilda.disconnectConfirm", "Отключить Tilda? Каталог товаров будет удалён из ИИ."))) return;
    setBusy(true);
    try { await axios.delete(`${API}/tilda/disconnect`); setSt({ connected: false }); setKey(""); setProducts(null); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const search = async () => {
    const r = await axios.get(`${API}/tilda/products`, { params: { q } });
    setProducts(r.data.products || []);
  };

  if (!st) return null;

  return (
    <div data-testid="tilda-mcp-block" className="mb-4 rounded-xl border-2 border-black p-4 bg-[#FFF7ED]">
      <div className="flex items-center gap-2 mb-1">
        <Package className="w-4 h-4" />
        <span className="font-semibold">{tf("tilda.title", "Интернет-магазин Tilda (MCP)")}</span>
        {st.connected && <CheckCircle2 data-testid="tilda-connected-badge" className="w-4 h-4 text-green-600" />}
      </div>
      <p className="text-xs text-gray-700 mb-3">{tf("tilda.desc", "ИИ будет отвечать клиентам по товарам, ценам и наличию из вашего каталога Tilda и направлять в магазин за покупкой.")}</p>

      {!st.connected ? (
        <>
          <ol className="text-xs text-gray-700 list-decimal ml-4 mb-3 space-y-0.5">
            <li>{tf("tilda.step1", "В Tilda откройте Профиль → «MCP server» → «New key»")}</li>
            <li>{tf("tilda.step2", "Выберите проект магазина, доступ «Limited» → отметьте только «Product catalog» (чтение) и срок «No expiration»")}</li>
            <li>{tf("tilda.step3", "Скопируйте ключ (показывается один раз) и вставьте ниже")}</li>
          </ol>
          <Label htmlFor="tilda_mcp_key" className="text-sm font-medium">{tf("tilda.keyLabel", "Ключ Tilda MCP")}</Label>
          <div className="flex gap-2 mt-1">
            <Input id="tilda_mcp_key" data-testid="tilda-mcp-key-input" type="password" autoComplete="off" value={key}
              onChange={(e) => setKey(e.target.value)} placeholder="mcp_tilda_…" className="border-2 border-black font-mono text-sm" />
            <Button data-testid="tilda-connect-btn" onClick={connect} disabled={busy || !key.trim().startsWith("mcp_tilda_")} className="bg-black text-white hover:bg-gray-800">
              {busy ? tf("tilda.checking", "Проверяем…") : tf("tilda.connect", "Подключить")}
            </Button>
          </div>
          <a href="https://help.tilda.cc/mcp" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 underline mt-2">
            {tf("tilda.help", "Инструкция Tilda по MCP")} <ExternalLink className="w-3 h-3" />
          </a>
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-2 text-sm mb-3">
            <div data-testid="tilda-project">
              <span className="text-gray-500">{tf("tilda.project", "Проект")}: </span>
              {st.projects?.length > 1 ? (
                <select data-testid="tilda-project-select" value={st.project_id || ""} onChange={(e) => patch({ project_id: e.target.value })} className="border rounded px-1 py-0.5 text-sm">
                  {st.projects.map((p) => <option key={p.id} value={p.id}>{p.title || p.url || p.id}</option>)}
                </select>
              ) : <b>{st.project_title || st.project_id}</b>}
              {st.project_url && <a href={st.project_url} target="_blank" rel="noreferrer" className="ml-1 text-blue-700 inline-flex"><ExternalLink className="w-3 h-3" /></a>}
            </div>
            <div data-testid="tilda-products-count"><span className="text-gray-500">{tf("tilda.products", "Товаров")}: </span><b>{st.products_count}</b></div>
            <div data-testid="tilda-synced-at"><span className="text-gray-500">{tf("tilda.syncedAt", "Обновлено")}: </span>{st.synced_at ? new Date(st.synced_at).toLocaleString() : "—"}</div>
            <div>
              <span className="text-gray-500">{tf("tilda.currency", "Валюта цен")}: </span>
              <select data-testid="tilda-currency-select" value={st.currency || "EUR"} onChange={(e) => patch({ currency: e.target.value })} className="border rounded px-1 py-0.5 text-sm">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 text-xs text-gray-500">{tf("tilda.key", "Ключ")}: <span className="font-mono">{st.key_masked}</span></div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="tilda-send-links-label">
              <input type="checkbox" data-testid="tilda-send-links-toggle" checked={st.send_product_links !== false}
                onChange={(e) => patch({ send_product_links: e.target.checked })} disabled={busy} className="w-4 h-4 accent-black" />
              {tf("tilda.sendLinks", "Разрешить ИИ отправлять клиенту ссылку на товар")}
            </label>
          </div>
          {st.last_error && <p data-testid="tilda-last-error" className="text-xs text-red-600 mb-2">{tf("tilda.lastError", "Последняя ошибка")}: {st.last_error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button data-testid="tilda-sync-btn" size="sm" variant="outline" onClick={sync} disabled={busy} className="border-black">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />{tf("tilda.sync", "Обновить каталог")}
            </Button>
            <Button data-testid="tilda-disconnect-btn" size="sm" variant="outline" onClick={disconnect} disabled={busy} className="border-red-300 text-red-700">
              <Unplug className="w-3.5 h-3.5 mr-1" />{tf("tilda.disconnect", "Отключить")}
            </Button>
          </div>
          <div className="mt-3 flex gap-2">
            <Input data-testid="tilda-product-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder={tf("tilda.searchPh", "Проверить товар в каталоге…")} className="text-sm" />
            <Button data-testid="tilda-product-search-btn" size="sm" variant="outline" onClick={search}>{tf("tilda.search", "Найти")}</Button>
          </div>
          {products && (
            <ul data-testid="tilda-product-results" className="mt-2 max-h-48 overflow-auto text-xs divide-y bg-white rounded border">
              {products.length === 0 && <li className="p-2 text-gray-500">{tf("tilda.noResults", "Ничего не найдено")}</li>}
              {products.map((p) => (
                <li key={p.uid} className="p-2 flex items-center gap-2">
                  {p.image && <img src={p.image} alt="" className="w-8 h-8 rounded object-cover" />}
                  <span className="flex-1">
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="underline hover:text-blue-700">{p.title}</a> : p.title}{p.sku ? ` [${p.sku}]` : ""}
                  </span>
                  <span className="font-mono">{p.price_min}{p.price_max && p.price_max !== p.price_min ? `–${p.price_max}` : ""} {st.currency}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-gray-500 mt-2">{tf("tilda.autoSync", "Каталог обновляется автоматически каждые 12 часов.")}</p>
        </>
      )}
    </div>
  );
}
